/**
 * Vendored super-admin CASL ability factory.
 *
 * This mirrors `@viq-backend/shared`'s `defineSuperAdminAbility` exactly —
 * same Action/Subject vocabulary, same role-to-rule mapping. It is
 * duplicated here because viq-admin (a separate repo) cannot take a
 * workspace dep on viq-backend. The spec notes: "vocabulary is shared;
 * in practice the SPA may vendor it."
 *
 * If the backend shared factory changes, keep this in sync manually.
 * A future shared-component-library extraction (noted as a plan-level
 * open item in the Phase 1 spec) could replace this vendored copy.
 */

import {
  AbilityBuilder,
  createMongoAbility,
  MongoAbility,
} from '@casl/ability';

// Actions mirroring the backend shared vocab.
export type Action = 'manage' | 'create' | 'read' | 'update' | 'delete';

// Subjects mirroring the backend shared vocab.
export type Subject =
  | 'all'
  | 'Company'
  | 'Conference'
  | 'User'
  | 'Lead'
  | 'Toolkit'
  | 'CorePrompt'
  | 'PromptTemplate'
  | 'Integration'
  | 'DiscoveryQuestion';

export type AppAbility = MongoAbility<[Action, Subject]>;

export type SuperAdminRole = 'super_admin' | 'super_admin_readonly';

interface DefineSuperAdminAbilityInput {
  roles: SuperAdminRole[];
  oid: string;
  email?: string;
}

/**
 * Build a CASL ability from the signed-in user's super-admin roles.
 *
 * Rules (mirrors backend):
 *   super_admin            → can('manage', 'all')  [full CRUD on all subjects]
 *   super_admin_readonly   → can('read', 'all') + can('update', 'Lead')
 *                            [read-only across all subjects, plus edit leads]
 *   both roles             → manage wins (union)
 *
 * The returned ability is UX-only — the backend `requireAbility` middleware
 * is the real gate. `<Can>` here drives what the SPA renders, nothing more.
 */
export function defineSuperAdminAbility({
  roles,
}: DefineSuperAdminAbilityInput): AppAbility {
  const { can, build } = new AbilityBuilder<AppAbility>(createMongoAbility);

  if (roles.includes('super_admin')) {
    can('manage', 'all');
  } else if (roles.includes('super_admin_readonly')) {
    can('read', 'all');
    // Leads-only edit carve-out (viq-admin#13 / viq-backend#52): a read-only
    // super-admin may edit a lead's own fields — but nothing else destructive
    // (no manage/delete Lead, no writes to any other subject). The backend
    // `requireAbility('update','Lead')` gate on the lead-edit endpoint is the
    // real enforcement; this mirror only keeps the SPA's <Can> rendering in step.
    can('update', 'Lead');
  }
  // No roles → empty ability (no rules → nothing permitted).

  return build();
}
